/**
 *  TopbarMobileMenu prints the menu content with Owner/Renter toggle
 * and expandable sections for different user types.
 */
import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { FaWhatsapp, FaRobot } from 'react-icons/fa';
import { SiCalendly } from 'react-icons/si';
import { AIChatWidget } from '../../../../components/AIChat/AIChatWidget';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  ExternalLink,
  InlineTextButton,
  NamedLink,
} from '../../../../components';
import { clearScrapedData } from '../../../EditListingPage/EditListingPage.duck';

import css from './TopbarMobileMenu.module.css';

/**
 * Expandable menu section component
 */
const ExpandableSection = ({ title, children, defaultOpen = false, contentClassName }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={css.expandableSection}>
      <button
        className={css.sectionHeader}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span>{title}</span>
        <span className={css.expandIcon}>{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div className={[css.sectionContent, contentClassName].filter(Boolean).join(' ')}>
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * Owner Menu Structure
 */
const OwnerMenu = ({ isAuthenticated, isAdmin }) => {
  return (
    <div className={css.menuContent}>
      {/* My Account Section */}
      <ExpandableSection
        title={<FormattedMessage id="TopbarMobileMenu.myAccount" />}
        defaultOpen={!isAuthenticated}
      >
        {isAuthenticated ? (
          <>
            <NamedLink name="ProfileSettingsPage" className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.profileSettings" />
            </NamedLink>
            <NamedLink name="AccountSettingsPage" className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.accountSettings" />
            </NamedLink>
            <NamedLink name="ManageListingsPage" params={{ tab: 'other' }} className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.manageListings" />
            </NamedLink>
            <NamedLink name="InboxPage" params={{ tab: 'sales' }} className={css.menuLink}>
              <FormattedMessage id="TopbarDesktop.manageBookingsLink" />
            </NamedLink>
            <NamedLink name="InboxPage" params={{ tab: 'sales' }} className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.inbox" />
            </NamedLink>
            {isAdmin ? (
              <NamedLink name="DashboardPage" className={css.menuLink}>
                <FormattedMessage id="TopbarMobileMenu.dashboardPageLink" />
              </NamedLink>
            ) : null}
          </>
        ) : (
          <>
            <NamedLink name="LoginPage" className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.login" />
            </NamedLink>
            <ExpandableSection
              title={<FormattedMessage id="TopbarMobileMenu.signup" />}
              defaultOpen={false}
            >
              <NamedLink name="GopilotPage" className={css.subMenuLink}>
                <FormattedMessage id="TopbarMobileMenu.gopilotFasterListing" />
              </NamedLink>
              {/* COMMENTED OUT - Manual Listing option (keep for potential future restoration)
              <NamedLink name="SignupPage" className={css.subMenuLink}>
                <FormattedMessage id="TopbarMobileMenu.manualListing" />
              </NamedLink>
              */}
            </ExpandableSection>
          </>
        )}
      </ExpandableSection>

      <div className={css.divider} />

      {/* How It Works Section */}
      <ExpandableSection
        title={<FormattedMessage id="TopbarMobileMenu.howItWorks" />}
        defaultOpen={false}
      >
        <NamedLink name="CMSPage" params={{ pageId: 'how-it-works-owners' }} className={css.menuLink}>
          <FormattedMessage id="TopbarMobileMenu.ownersFAQs" />
        </NamedLink>
        <NamedLink name="EarningsCalculatorPage" className={css.menuLink}>
          <FormattedMessage id="TopbarMobileMenu.earningsCalculator" />
        </NamedLink>
        <NamedLink name="CMSPage" params={{ pageId: 'meet_the_owner' }} className={css.menuLink}>
          <FormattedMessage id="TopbarMobileMenu.whyGoGallivanting" />
        </NamedLink>
        <NamedLink name="CMSPage" params={{ pageId: 'blog' }} className={css.menuLink}>
          <FormattedMessage id="TopbarMobileMenu.roadTripReady" />
        </NamedLink>
      </ExpandableSection>

      <div className={css.divider} />

      {/* Contact Us Section */}
      <ExpandableSection
        title={<FormattedMessage id="TopbarMobileMenu.contactUs" />}
        defaultOpen={false}
        contentClassName={css.supportContent}
      >
        <ExternalLink
          href="https://tally.so/r/wkB5Vj"
          className={css.menuLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage id="TopbarMobileMenu.contactForm" />
        </ExternalLink>
        <ExternalLink
          href="mailto:hello@go-gallivanting.co.uk?subject=GoGallivanting%20Contact&body=Name%3A%0D%0APhone%3A%0D%0ATopic%3A%20Listing%20%7C%20Earnings%20%7C%20Other%0D%0AMessage%3A"
          className={css.menuLink}
        >
          <FormattedMessage id="TopbarMobileMenu.emailUs" />
        </ExternalLink>
      </ExpandableSection>
    </div>
  );
};

/**
 * Renter Menu Structure
 */
const RenterMenu = ({ isAuthenticated, onChatClick, onCalendlyClick, onWhatsAppClick }) => {
  return (
    <div className={css.menuContent}>
      {/* My Account Section */}
      <ExpandableSection
        title={<FormattedMessage id="TopbarMobileMenu.myAccount" />}
        defaultOpen={false}
      >
        {isAuthenticated ? (
          <>
            <NamedLink name="ProfileSettingsPage" className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.profileSettings" />
            </NamedLink>
            <NamedLink name="AccountSettingsPage" className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.accountSettings" />
            </NamedLink>
            <NamedLink name="WishlistPage" className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.wishlist" />
            </NamedLink>
            <NamedLink name="InboxPage" params={{ tab: 'orders' }} className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.inbox" />
            </NamedLink>
            {/* <NamedLink className={classNames(css.navigationLink, currentPageClass('ProviderEscalationPage'))} name="ProviderEscalationPage">
              <FormattedMessage id="TopbarMobileMenu.customerQuestionsLink" />
            </NamedLink> */}
          </>
        ) : (
          <>
            <NamedLink name="LoginPage" className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.login" />
            </NamedLink>
            <NamedLink name="SignupPage" className={css.menuLink}>
              <FormattedMessage id="TopbarMobileMenu.signup" />
            </NamedLink>
          </>
        )}
      </ExpandableSection>

      <div className={css.divider} />

      {/* Explore Section */}
      <ExpandableSection
        title={<FormattedMessage id="TopbarMobileMenu.explore" />}
        defaultOpen={false}
      >
        <NamedLink name="SearchPage" className={css.menuLink}>
          <FormattedMessage id="TopbarMobileMenu.searchMotorhomes" />
        </NamedLink>
        <NamedLink name="CMSPage" params={{ pageId: 'how-it-works-renters' }} className={css.menuLink}>
          <FormattedMessage id="TopbarMobileMenu.howItWorksRenters" />
        </NamedLink>
        <NamedLink name="CMSPage" params={{ pageId: 'meet_the_owner' }} className={css.menuLink}>
          <FormattedMessage id="TopbarMobileMenu.whyGoGallivanting" />
        </NamedLink>
        <NamedLink name="CMSPage" params={{ pageId: 'blog' }} className={css.menuLink}>
          <FormattedMessage id="TopbarMobileMenu.roadTripReady" />
        </NamedLink>
      </ExpandableSection>

      <div className={css.divider} />

      {/* Contact Us Section */}
      <ExpandableSection
        title={<FormattedMessage id="TopbarMobileMenu.contactUs" />}
        defaultOpen={false}
      >
        <ExternalLink
          href="https://tally.so/r/wkB5Vj"
          className={css.menuLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage id="TopbarMobileMenu.contactForm" />
        </ExternalLink>
        <ExternalLink
          href="mailto:hello@go-gallivanting.co.uk?subject=GoGallivanting%20Rental%20Enquiry&body=Name%3A%0D%0APhone%3A%0D%0ATrip%20Dates%3A%0D%0ADestination%3A%0D%0AMessage%3A"
          className={css.menuLink}
        >
          <FormattedMessage id="TopbarMobileMenu.emailUs" />
        </ExternalLink>
        <button className={css.supportMenuLink} type="button" onClick={onChatClick}>
          <FaRobot className={css.supportMenuIcon} />
          <FormattedMessage id="TopbarDesktop.supportChat" defaultMessage="Ask GoPilot™" />
        </button>
        <button className={css.supportMenuLink} type="button" onClick={onCalendlyClick}>
          <SiCalendly className={css.supportMenuIcon} />
          <FormattedMessage id="TopbarDesktop.supportCalendly" defaultMessage="Book Your GoLive Call" />
        </button>
        <button className={css.supportMenuLink} type="button" onClick={onWhatsAppClick}>
          <FaWhatsapp className={css.supportMenuIcon} />
          <FormattedMessage id="TopbarDesktop.supportWhatsApp" defaultMessage="WhatsApp" />
        </button>
      </ExpandableSection>
    </div>
  );
};

const CreateListingMenuMobile = () => {
  // const [open, setOpen] = useState(false); // COMMENTED OUT - Dropdown state no longer needed
  const dispatch = useDispatch();

  // const toggle = () => setOpen(o => !o); // COMMENTED OUT - Toggle function no longer needed

  // Handler to clear scraped data before navigating to GoPilot
  const handleGoPilotClick = () => {
    dispatch(clearScrapedData());
    console.log('🧹 Navigating to GoPilot (mobile) - clearing scraped data');
  };

  /* COMMENTED OUT - Old dropdown implementation (keep for potential future restoration)

  const handleManualClick = () => {
    dispatch(clearScrapedData());
    console.log('🧹 Manual listing selected (mobile) - clearing scraped data');
  };

  const dropdownStyle = {
    marginTop: 8,
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  };

  const itemStyle = {
    display: 'block',
    color: 'white',
    textDecoration: 'none',
  };

  return (
    <div>
      <InlineTextButton className={css.createNewListingLink} onClick={toggle}>
        <FormattedMessage id="TopbarMobileMenu.newListingLink" />
      </InlineTextButton>
      {open ? (
        <div style={dropdownStyle}>
          <div onClick={handleManualClick}>
            <NamedLink name="NewListingPage" className={css.createNewListingLink} style={itemStyle}>
              <FormattedMessage id="TopbarMobileMenu.manualListing" />
            </NamedLink>
          </div>
          <NamedLink name="GopilotPage" className={css.createNewListingLink} style={{ ...itemStyle, marginTop: 4 }}>
            <FormattedMessage id="TopbarMobileMenu.gopilotFasterListing" />
          </NamedLink>
        </div>
      ) : null}
    </div>
  );
  */

  // NEW: Direct GoPilot link implementation
  return (
    <div onClick={handleGoPilotClick}>
      <NamedLink name="GopilotPage">
        <InlineTextButton className={css.createNewListingLink}>
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </InlineTextButton>
      </NamedLink>
    </div>
  );
};

/**
 * Main Mobile Menu Component
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentUser,
    onLogout,
    isListingPage = false,
    currentListing = null,
    listingReviews = [],
  } = props;

  const [chatOpen, setChatOpen] = useState(false);
  const WA_URL =
    'https://wa.me/447501526140?text=Hi%20%E2%80%94%20I%27m%20listing%20my%20motorhome%20on%20GoGallivanting%20and%20have%20a%20question.';
  const CALENDLY_URL = 'https://calendly.com/gogallivanting/30min';

  const loadCalendlyScript = () => {
    return new Promise(resolve => {
      if (window.Calendly) {
        resolve();
        return;
      }
      if (document.querySelector('script[data-calendly]')) {
        const existing = document.querySelector('script[data-calendly]');
        existing.addEventListener('load', () => resolve());
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.setAttribute('data-calendly', 'true');
      script.onload = () => resolve();
      document.body.appendChild(script);
    });
  };

  const openCalendly = () => {
    loadCalendlyScript().then(() => {
      if (window.Calendly) {
        window.Calendly.initPopupWidget({ url: CALENDLY_URL });
      }
    });
  };

  const handleWhatsApp = () => {
    window.open(WA_URL, '_blank', 'noopener noreferrer');
  };

  const handleChat = () => {
    setChatOpen(true);
  };


  const user = ensureCurrentUser(currentUser);
  const userType = user?.attributes?.profile?.publicData?.userType;
  const isAdmin = user?.attributes?.profile?.publicData?.isAdmin;

  // Default view is "Owners" for non-authenticated users
  // For authenticated users, show only their user type
  const [viewMode, setViewMode] = useState('owners');

  // Determine which menu to show
  let showOwnerMenu = false;
  let showRenterMenu = false;
  let showToggle = true;

  if (isAuthenticated) {
    // Authenticated: show only their menu based on userType
    showToggle = false;
    if (userType === 'customer') {
      showRenterMenu = true;
    } else {
      // owner or other types default to owner menu
      showOwnerMenu = true;
    }
  } else {
    // Not authenticated: show toggle and respect viewMode
    showToggle = true;
    if (viewMode === 'owners') {
      showOwnerMenu = true;
    } else {
      showRenterMenu = true;
    }
  }

  return (
    <>
      <div className={css.root}>
        <div className={css.content}>
          {/* Owner/Renter Toggle - Only show when not authenticated */}
          {showToggle && (
            <div className={css.toggleContainer}>
              <button
                className={classNames(css.toggleButton, viewMode === 'owners' && css.toggleButtonActive)}
                onClick={() => setViewMode('owners')}
                type="button"
              >
                <FormattedMessage id="TopbarMobileMenu.toggleOwners" />
              </button>
              <button
                className={classNames(css.toggleButton, viewMode === 'renters' && css.toggleButtonActive)}
                onClick={() => setViewMode('renters')}
                type="button"
              >
                <FormattedMessage id="TopbarMobileMenu.toggleRenters" />
              </button>
            </div>
          )}

          {/* Show logout button if authenticated */}
          {isAuthenticated && (
            <button className={css.logoutButton} onClick={onLogout} type="button">
              <FormattedMessage id="TopbarMobileMenu.logout" />
            </button>
          )}

          {/* Render appropriate menu */}
          {showOwnerMenu && <OwnerMenu isAuthenticated={isAuthenticated} isAdmin={isAdmin} />}
          {showRenterMenu && (
            <RenterMenu
              isAuthenticated={isAuthenticated}
              isAdmin={isAdmin}
              onChatClick={handleChat}
              onCalendlyClick={openCalendly}
              onWhatsAppClick={handleWhatsApp}
            />
          )}

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>
          {showOwnerMenu && <CreateListingMenuMobile />}
          {showRenterMenu && (
            <NamedLink name="SearchPage" className={css.createNewListingLink}>
              <FormattedMessage id="TopbarMobileMenu.browseListings" />
            </NamedLink>
          )}
        </div>
      </div>
      {chatOpen && (
        <AIChatWidget
          key={isListingPage ? `ctx-${currentListing?.id?.uuid}` : 'general'}
          listingData={isListingPage ? currentListing : null}
          reviews={isListingPage ? listingReviews : []}
          tenantId={process.env.REACT_APP_AYAKAN_AI_CLIENT_ID}
          userId={currentUser?.id?.uuid || null}
          theme="light"
          primaryColor="303b4b"
          mode={isListingPage ? 'context-chat' : 'general-chat'}
          title={isListingPage ? 'Ask GoPilot™' : 'GoPilot™ — Here to help'}
          hideTrigger
          isOpen={chatOpen}
          onOpenChange={setChatOpen}
        />
      )}
    </>
  );
};

export default TopbarMobileMenu;
